/**
 * Payment processor utilities
 * Shared functions for handling payment processor logic across donation forms
 */

import { FINIX_DEFAULT_MAX_TRANSACTION_AMOUNT, FINIX_LEGACY_MAX_TRANSACTION_AMOUNT, PAYMENT_PROCESSOR_LIMITS } from '../constants/validation.js';

/**
 * Calculate the maximum transaction amount from the payment processor and the server's value.
 *
 * The server (`/elements-config`) sends `maxTransactionAmountIsProcessorLimit: true` when its
 * `maxTransactionAmount` is the processor's real per-charge limit. Only then is a FINIX value
 * trusted uncapped (each nonprofit has its own Finix limit). Without the flag the value may be
 * a stamped form setting, so FINIX keeps the legacy 18999.99 cap. STRIPE and unknown processors
 * are always clamped to their fixed limit.
 *
 * @param {string} processor - Payment processor name ("STRIPE", "FINIX", etc.)
 * @param {number|undefined} backendMaxAmount - Server-provided maximum
 * @param {boolean|undefined} isProcessorLimit - Server flag: the value is the processor's real limit
 * @returns {number} Maximum allowed transaction amount
 *
 * @example
 * calculateMaxTransactionAmount('STRIPE', 1000000)        // 999999.99 (Stripe hard limit)
 * calculateMaxTransactionAmount('FINIX', 30000, true)     // 30000 (nonprofit's own limit binds)
 * calculateMaxTransactionAmount('FINIX', 30000)           // 18999.99 (legacy cap, no flag)
 * calculateMaxTransactionAmount('FINIX', undefined, true) // 18000 (no usable server value)
 */
export function calculateMaxTransactionAmount(processor, backendMaxAmount, isProcessorLimit) {
  const hasServerValue = Number.isFinite(backendMaxAmount) && backendMaxAmount > 0;

  if (processor === 'FINIX') {
    if (isProcessorLimit === true) {
      return hasServerValue ? backendMaxAmount : FINIX_DEFAULT_MAX_TRANSACTION_AMOUNT;
    }
    return hasServerValue
      ? Math.min(FINIX_LEGACY_MAX_TRANSACTION_AMOUNT, backendMaxAmount)
      : FINIX_LEGACY_MAX_TRANSACTION_AMOUNT;
  }

  const processorLimit = PAYMENT_PROCESSOR_LIMITS[processor] || PAYMENT_PROCESSOR_LIMITS.DEFAULT;
  return hasServerValue ? Math.min(processorLimit, backendMaxAmount) : processorLimit;
}

/**
 * The maximum the checkout enforces, read from its `/elements-config` response.
 *
 * A FINIX value, or one with no processor named, goes through calculateMaxTransactionAmount so
 * an unflagged value (possibly a stamped form setting) keeps the legacy Finix cap. Any other
 * processor's value, and an absent value, pass through unchanged, as they did before the flag.
 *
 * @param {object} responseData - The checkout's /elements-config response
 * @returns {number|undefined} The maximum, or the raw server value
 */
export function resolveCheckoutMaxTransactionAmount(responseData) {
  const processor = responseData['defaultPaymentProcessor'] ?? 'FINIX';
  const serverMax = responseData['maxTransactionAmount'];
  if (processor === 'FINIX' && serverMax != null) {
    return calculateMaxTransactionAmount('FINIX', serverMax, responseData['maxTransactionAmountIsProcessorLimit']);
  }
  return serverMax;
}

/**
 * Format a payment processor name for display
 * @param {string} processor - Payment processor name
 * @returns {string} Formatted processor name
 */
export function formatProcessorName(processor) {
  if (!processor) return 'Unknown';
  return processor.charAt(0).toUpperCase() + processor.slice(1).toLowerCase();
}
